import React, { useEffect, useState } from 'react';
import { Modal, ConfigProvider, Tag } from 'antd';
import {
    GetAllFormularios,
    GetByIdFormulario,
    SaveFormulario
} from '@services/Formulario/Index';
import { formatDateTimeToBR } from '@utils/FormatDate';
import { StyledTable } from '@styles/datatables/datatable-antd';
import TextBlock from '@components/UI/TextBlock/Index.';
import ButtonComp from '@components/UI/Button/Index';
import { useDispatch } from 'react-redux';
import SweetAlert from '@components/Feedback/SweetAlert/Index';
import { carregarFormularioAssociadoProcesso } from '@store/slices/formularios/Processo/formularioAssociadoProcessoSlice';
import ptBR from 'antd/es/locale/pt_BR';
import { SurveyPreview } from '../SurveyPreviewModal/Index';

export const FormularioSelectorProcessoModal = ({
    visible,
    onClose,
    idProcesso,
    idFormularioAssociado,
}) => {
    const dispatch = useDispatch();
    const modoVisualizacao = Boolean(idFormularioAssociado);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [formularios, setFormularios] = useState([]);
    const [loading, setLoading] = useState(false);

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewJson, setPreviewJson] = useState(null);

    useEffect(() => {
        if (visible) fetchFormularios();
        else setSelectedRowKeys([]);
    }, [visible]);

    const fetchFormularios = async () => {
        setLoading(true);
        try {
            if (modoVisualizacao && idFormularioAssociado) {
                const associado = await GetByIdFormulario(idFormularioAssociado);

                setFormularios(associado ? [associado] : []);
            } else {
                const { items } = await GetAllFormularios(1, 100, { flgModelo: true });
                setFormularios(items);
            }
        } catch (error) {
            console.error(error);
            setFormularios([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAssociate = async () => {
        if (!selectedRowKeys.length) {
            SweetAlert.toastInfo('Nenhum modelo selecionado.');
            return;
        }

        const confirmed = await SweetAlert.warning(
            'Deseja associar este formulário ao processo?',
            'Essa ação não poderá ser desfeita.'
        );

        if (!confirmed) return;

        const selectedId = selectedRowKeys[0];
        try {
            const selectedFormulario = formularios.find(f => f.id === selectedId);
            const response = await SaveFormulario({
                json: JSON.stringify(selectedFormulario.json),
                idProcesso,
                idModeloOrigem: selectedId,
                flgModelo: false,
                flgStatus: true,
            });

            dispatch(
                carregarFormularioAssociadoProcesso({
                    idProcesso,
                    formulario: response
                })
            );

            SweetAlert.toastSuccess('Formulário associado com sucesso!');
            onClose();
        } catch (err) {
            console.error(err);
            SweetAlert.toastError('Erro ao associar formulário.');
        }
    };

    const columns = [
        {
            title: 'CÓDIGO',
            dataIndex: 'codigo',
            key: 'codigo',
            width: '10%'
        },
        {
            title: 'TÍTULO',
            dataIndex: ['json', 'title'],
            key: 'titulo',
            width: '50%',
            render: text => <TextBlock>{text}</TextBlock>
        },
        {
            title: 'DATA CRIAÇÃO',
            dataIndex: 'dataCriacao',
            key: 'dataCriacao',
            render: formatDateTimeToBR
        },
        {
            title: 'STATUS',
            dataIndex: 'flgStatus',
            key: 'flgStatus',
            sorter: (a, b) => a.flgStatus - b.flgStatus,
            width: '10%',
            render: flg => (
                <Tag color={(flg) === true ? 'green' : 'red'}>
                    {(flg) === true ? 'ATIVO' : 'INATIVO'}
                </Tag>
            )
        },
        {
            title: 'VISUALIZAR',
            key: 'preview',
            render: (_, record) => (
                <ButtonComp
                    type="button"
                    title="VISUALIZAR"
                    hoverMessage="Visualizar questões do formulário"
                    size="small"
                    buttonColor="#125578"
                    classnameIcon="fa-regular fa-file-lines"
                    onClick={() => {
                        setPreviewJson(record.json);
                        setPreviewVisible(true);
                    }}
                />
            )
        }
    ];

    const rowSelection = !modoVisualizacao
        ? { type: 'radio', selectedRowKeys, onChange: setSelectedRowKeys }
        : undefined;

    return (
        <div>
            {/* modal principal */}
            <Modal
                getContainer={false}
                title={
                    modoVisualizacao
                        ? 'Formulário Associado (visualização)'
                        : 'Selecionar Formulário Modelo'
                }
                open={visible}
                onCancel={onClose}
                centered
                footer={
                    !modoVisualizacao && (
                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <ButtonComp
                                title="CANCELAR"
                                type="button"
                                buttonColor="#dc3545"
                                classnameIcon="fa-solid fa-xmark"
                                onClick={onClose}
                            />
                            <ButtonComp
                                title="ASSOCIAR"
                                type={"button"}
                                buttonColor="#2b97be"
                                classnameIcon="fa-regular fa-floppy-disk"
                                onClick={handleAssociate}
                            />
                        </div>
                    )
                }
                width="60%"
            >
                <h2>
                    {modoVisualizacao
                        ? '- Visualização do formulário já associado ao processo.'
                        : '- Selecione um modelo para associar ao processo.'}
                </h2>
                <ConfigProvider locale={ptBR}>
                    <StyledTable
                        rowKey="id"
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={formularios}
                        loading={loading}
                        pagination={{
                            defaultPageSize: 5,
                            showSizeChanger: true,
                            pageSizeOptions: [5, 10, 20, 50, 100],
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} de ${total}`
                        }}
                        locale={{
                            emptyText: <h3 className="m-3">Nenhum dado disponível.</h3>
                        }}
                        scroll={{ x: 'max-content' }}
                    />
                </ConfigProvider>
            </Modal>

            {/* modal de preview */}
            <Modal
                getContainer={false}
                title="Pré-visualização do Formulário"
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width="60%"
                centered
            >
                {previewJson ? (
                    <SurveyPreview json={previewJson} />
                ) : (
                    <p>Carregando pré-via...</p>
                )}
            </Modal>
        </div>
    );
};
